/**
 * @module engine3d/math3
 * @summary Minimal 3D linear-algebra kernel for the 3D view.
 * @description
 *   Zero-dependency vector / 4x4-matrix maths. Matrices are COLUMN-MAJOR
 *   (`m[col * 4 + row]`), the same convention WebGL uses, so the projection and
 *   look-at builders read like their textbook forms.
 *
 *   Coordinate system (right-handed, Y-up):
 *     +X → right across the plant floor   (maps from the 2D document's x)
 *     +Y → up                             (equipment height — new in 3D)
 *     +Z → toward the viewer              (maps from the 2D document's y)
 *   The floor is the plane Y = 0; every model sits on it.
 *
 *   // INVARIANT: nothing in this module touches engine state or the DOM.
 */

// ─── Vectors ─────────────────────────────────────────────────────────────

export interface Vec2 {
  x: number;
  y: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export const vAdd = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });

export const vSub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });

export const vMul = (a: Vec3, k: number): Vec3 => ({ x: a.x * k, y: a.y * k, z: a.z * k });

export const vDot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

export const vCross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

export const vLen = (a: Vec3): number => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);

/** Normalise, returning (0,1,0) for a degenerate zero-length input. */
export function vNorm(a: Vec3): Vec3 {
  const l = vLen(a);
  return l > 1e-9 ? { x: a.x / l, y: a.y / l, z: a.z / l } : { x: 0, y: 1, z: 0 };
}

/** Linear interpolation between two points. */
export const vLerp = (a: Vec3, b: Vec3, t: number): Vec3 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  z: a.z + (b.z - a.z) * t,
});

/**
 * Any unit vector perpendicular to `n`.
 * Picking the world axis *least* aligned with `n` keeps the cross product well
 * conditioned — the naive "always use Y" version collapses on vertical pipes,
 * which is exactly the case a plant layout is full of.
 */
export function perpendicular(n: Vec3): Vec3 {
  const ax = Math.abs(n.x);
  const ay = Math.abs(n.y);
  const az = Math.abs(n.z);
  const axis = ax < ay && ax < az ? v3(1, 0, 0) : ay < az ? v3(0, 1, 0) : v3(0, 0, 1);
  return vNorm(vCross(n, axis));
}

// ─── 4x4 matrices (column-major) ─────────────────────────────────────────

export type Mat4 = Float64Array;

export function mIdentity(): Mat4 {
  const m = new Float64Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

/** Matrix product `a · b` (apply b first, then a). */
export function mMul(a: Mat4, b: Mat4): Mat4 {
  const o = new Float64Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      o[c * 4 + r] =
        a[r] * b[c * 4] +
        a[4 + r] * b[c * 4 + 1] +
        a[8 + r] * b[c * 4 + 2] +
        a[12 + r] * b[c * 4 + 3];
    }
  }
  return o;
}

/**
 * Right-handed look-at view matrix.
 * `up` is a hint; it is re-orthogonalised against the view direction.
 */
export function mLookAt(eye: Vec3, target: Vec3, up: Vec3 = v3(0, 1, 0)): Mat4 {
  const f = vNorm(vSub(target, eye)); // forward
  let s = vCross(f, up); // right
  if (vLen(s) < 1e-6) s = perpendicular(f);
  s = vNorm(s);
  const u = vCross(s, f); // true up
  const m = new Float64Array(16);
  m[0] = s.x;
  m[4] = s.y;
  m[8] = s.z;
  m[1] = u.x;
  m[5] = u.y;
  m[9] = u.z;
  m[2] = -f.x;
  m[6] = -f.y;
  m[10] = -f.z;
  m[12] = -vDot(s, eye);
  m[13] = -vDot(u, eye);
  m[14] = vDot(f, eye);
  m[15] = 1;
  return m;
}

/** Transform a POINT (w = 1) by `m`, ignoring any perspective divide. */
export function xfPoint(m: Mat4, p: Vec3): Vec3 {
  return {
    x: m[0] * p.x + m[4] * p.y + m[8] * p.z + m[12],
    y: m[1] * p.x + m[5] * p.y + m[9] * p.z + m[13],
    z: m[2] * p.x + m[6] * p.y + m[10] * p.z + m[14],
  };
}

/** Transform a DIRECTION (w = 0) by `m` — translation is skipped. */
export function xfDir(m: Mat4, p: Vec3): Vec3 {
  return {
    x: m[0] * p.x + m[4] * p.y + m[8] * p.z,
    y: m[1] * p.x + m[5] * p.y + m[9] * p.z,
    z: m[2] * p.x + m[6] * p.y + m[10] * p.z,
  };
}

// ─── Scalar helpers ──────────────────────────────────────────────────────

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const TAU = Math.PI * 2;

export const deg = (d: number): number => (d * Math.PI) / 180;

/**
 * Convex hull (monotone chain) of a 2D point set, counter-clockwise.
 * Used to turn a scattered set of projected silhouette points into the single
 * polygon that is the object's ground shadow.
 */
export function convexHull(pts: Vec2[]): Vec2[] {
  if (pts.length < 3) return pts.slice();
  const p = pts.slice().sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  const cross = (o: Vec2, a: Vec2, b: Vec2) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Vec2[] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0)
      lower.pop();
    lower.push(q);
  }
  const upper: Vec2[] = [];
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0)
      upper.pop();
    upper.push(q);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}


// ─── Ray intersections ───────────────────────────────────────────────────
//
// Both are used to turn a mouse ray into a position: an axis handle takes the
// closest point on its axis, a plane handle takes the plane crossing. They live
// here rather than beside their callers because the pipe tool and the gizmo
// both need them, and neither should have to import the other.

/**
 * Closest point on the infinite line (`p`, `a`) to the ray (`o`, `d`).
 * Returns null when the two are near-parallel, where the answer is unstable and
 * the point would shoot off to infinity.
 */
export function rayAxisPoint(o: Vec3, d: Vec3, p: Vec3, a: Vec3): Vec3 | null {
  const w0 = vSub(o, p);
  const B = vDot(d, a);
  const denom = 1 - B * B;
  if (Math.abs(denom) < 1e-4) return null;
  const D = vDot(d, w0);
  const E = vDot(a, w0);
  const t = (E - B * D) / denom;
  return vAdd(p, vMul(a, t));
}

/** Intersection of the ray with the plane through `p` with normal `n`. */
export function rayPlanePoint(o: Vec3, d: Vec3, p: Vec3, n: Vec3): Vec3 | null {
  const den = vDot(d, n);
  if (Math.abs(den) < 1e-5) return null;
  const t = vDot(vSub(p, o), n) / den;
  if (t < 0) return null;
  return vAdd(o, vMul(d, t));
}
